import { useEffect, useRef, useState } from 'react';
import ErrorMessage from './ErrorMessage';
import { DeploymentLogLevel } from '@back4app2/sdk';
import LoadingSpinner from './LoadingSpinner';
import LogOverlay from "./LogOverlay";
import LogLevelBadge, { LogLevel } from './ds/LogLevelBadge';
import { ExpandIcon } from './ds/icons';
import JumpToLatestButton from './JumpToLatestButton';
import LogsUnavailable from './LogsUnavailable';

export type LogBoxProps = {
  errorMessage?: string;
  logs?: {
    id: string;
    time: Date;
    message: string;
    level: DeploymentLogLevel;
  }[];
  isLoading: boolean;
  canMaximize?: boolean;
  /** Fills its container instead of the default 26rem — the Logs screen gives
   * the well the whole page, the overview gives it a card. */
  fullHeight?: boolean;
  /** The deployment finished building: no lines means they were deleted,
   * not that they are yet to come. */
  hasFinished?: boolean;
}

const LogBox = ({ errorMessage, logs, isLoading, canMaximize, fullHeight, hasFinished }: LogBoxProps) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      if (!errorMessage && logs && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
    },
    [
      errorMessage,
      logs,
      autoScroll
    ]
  );


  // Scrolling to the end is what re-arms following: onScroll sees the bottom.
  const jumpToLatest = () => {
    if (boxRef.current) {
      // Instant: a smooth scroll passes through positions short of the end,
      // each of which would bring the button back for a moment.
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
    setAutoScroll(true);
  };

  const onScroll = () => {
    if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) >= 0.99 * boxRef.current.scrollHeight && !autoScroll) {
      setAutoScroll(true);
    } else if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) < 0.99 * boxRef.current.scrollHeight && autoScroll) {
      setAutoScroll(false);
    }
  };

  /**
   * Expanded, not full-screen.
   *
   * `requestFullscreen` handed the whole viewport to a bare <div>, which cost
   * us the browser's own "Press Esc to exit full screen" overlay — a message
   * no script of ours can dismiss — and left the exit control stranded inside
   * a scrolling box. Our own overlay costs nothing, puts the control in the
   * corner where it belongs, and closes on Escape like every other layer.
   */
  const handleFullScreen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isLoading || errorMessage) {
      return;
    }
    e.preventDefault();
    setIsMaximized(true);
  }

  const exitFullScreen = () => setIsMaximized(false);

  useEffect(() => {
    if (!isMaximized) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMaximized(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMaximized]);

  /**
   * One line per entry: timestamp, level, message. The message wraps instead
   * of pushing the row sideways — a build log read on a phone was unreadable
   * when the only way through it was a horizontal scrollbar.
   *
   * The time column is sized to `HH:MM:SS.mmm` and no wider: at 132px it
   * carried 40px of nothing on every row, which is the width the message was
   * wrapping for.
   */
  const renderLines = (inModal: boolean) => logs && logs.map((log, i) => (
    <div
      key={log.id}
      className={[
        // A phone: time and level on one line, the message under them.
        'flex flex-wrap items-center gap-x-2 gap-y-1 px-3.5 py-[5px] sm:flex-nowrap sm:items-start sm:gap-3',
        i % 2 ? 'bg-white/[0.015]' : '',
      ].join(' ')}
    >
      <span className="font-courrier text-ds-115 text-navy-300 flex-none sm:w-[96px]">
        {log.time.toISOString().slice(11, 23)}
      </span>
      <LogLevelBadge level={log.level.toLowerCase() as LogLevel} fixedWidth={!inModal} />
      <span className="font-courrier text-ds-115 leading-[1.5] text-navy-100 basis-full sm:basis-auto flex-1 min-w-0 break-words">
        {log.message}
      </span>
    </div>
  ));

  if (isMaximized) {
    return (
      <LogOverlay label="deployment log" onClose={exitFullScreen}>
        {errorMessage ? <ErrorMessage message={errorMessage} /> : renderLines(true)}
      </LogOverlay>
    );
  }

  return (<div className={`relative ${fullHeight ? 'h-full' : ''}`}>
    {canMaximize && !isMaximized ? (
      <button
        type="button"
        aria-label="Expand log"
        onClick={isMaximized ? exitFullScreen : handleFullScreen}
        className="b4-focus z-10 absolute top-2.5 right-2.5 w-[30px] h-[30px] rounded-control border border-navy-400 bg-navy-700 text-navy-100 flex items-center justify-center hover:bg-navy-600 transition-colors duration-[120ms]"
      >
        <ExpandIcon size={14} />
      </button>
    ) : null}
    <div
      className="relative py-1.5 bg-navy-950 overflow-y-auto b4-thin"
      ref={boxRef}
      // onScroll, not onWheel: dragging the scrollbar or using the keyboard
      // moves away from the end too, and has to stop the following.
      onScroll={onScroll}
      style={{ height: fullHeight ? '100%' : '26rem' }}
    >
      {isLoading ? <div className="flex h-full justify-center items-center"><LoadingSpinner color="text-white" /></div> : null}
      {errorMessage ? (
        <div className="flex h-full"><ErrorMessage message={errorMessage} /></div>
      ) : !isLoading && logs && logs.length === 0 ? (
        hasFinished ? (
          <LogsUnavailable fill />
        ) : (
          <div className="flex h-full justify-center items-center py-10 text-ds-135 text-navy-300">No log lines yet.</div>
        )
      ) : renderLines(false)}
    </div>
    {!autoScroll && !isLoading && !errorMessage && logs && logs.length ? (
      <JumpToLatestButton onClick={jumpToLatest} />
    ) : null}
  </div>);
}

export default LogBox;
